import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppAlert, type AppAlertType } from '../components/AppAlert';

interface AlertContextValue {
  /** Show a single-button informational dialog. Optional callback fires on close. */
  showAlert: (message: string, callback?: () => void) => void;
  /** Show a two-button yes/no dialog. callback receives the user's choice. */
  showConfirm: (message: string, callback: (confirmed: boolean) => void) => void;
}

const AlertContext = createContext<AlertContextValue | null>(null);

interface AlertState {
  open: boolean;
  message: string;
  type: AppAlertType;
}

interface AlertProviderProps {
  children: React.ReactNode;
}

/**
 * Provides platform-independent alert/confirm dialogs.
 * Replaces tg.showAlert / tg.showConfirm with a custom modal so the same
 * code works on both Telegram and MAX (which has no native alert API).
 */
export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [state, setState] = useState<AlertState>({
    open: false,
    message: '',
    type: 'alert',
  });

  // Resolver is stored in a ref so updating it does not trigger re-renders.
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const showAlert = useCallback((message: string, callback?: () => void) => {
    resolverRef.current = callback ? () => callback() : null;
    setState({ open: true, message, type: 'alert' });
  }, []);

  const showConfirm = useCallback(
    (message: string, callback: (confirmed: boolean) => void) => {
      resolverRef.current = callback;
      setState({ open: true, message, type: 'confirm' });
    },
    []
  );

  const handleClose = useCallback((confirmed: boolean) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
    // Call resolver after state update so any consumer-triggered re-renders
    // see the modal as already closed.
    if (resolver) {
      resolver(confirmed);
    }
  }, []);

  const value = useMemo<AlertContextValue>(
    () => ({ showAlert, showConfirm }),
    [showAlert, showConfirm]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      <AppAlert
        open={state.open}
        message={state.message}
        type={state.type}
        onClose={handleClose}
      />
    </AlertContext.Provider>
  );
};

/** Consumer hook for showAlert / showConfirm. Must be used within AlertProvider. */
export const useAppAlert = (): AlertContextValue => {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    throw new Error('useAppAlert must be used within an AlertProvider');
  }
  return ctx;
};
